import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Input,
  Option,
} from '@fluentui/react-components';
import { WarningRegular } from '@fluentui/react-icons';
import { type UseMutationResult } from '@tanstack/react-query';

export interface RecordPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recordPaymentMutation: UseMutationResult<unknown, Error, { amount: number; paymentType: 'check' | 'cash'; reference?: string }>;
}

export function RecordPaymentDialog({
  open,
  onOpenChange,
  recordPaymentMutation,
}: RecordPaymentDialogProps) {
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentType, setPaymentType] = useState<'check' | 'cash'>('check');
  const [paymentReference, setPaymentReference] = useState('');

  const handleCancel = () => {
    onOpenChange(false);
    setPaymentAmount('');
    setPaymentReference('');
  };

  const handleSubmit = () => {
    const amount = parseFloat(paymentAmount);
    if (amount > 0) {
      recordPaymentMutation.mutate(
        {
          amount,
          paymentType,
          reference: paymentReference || undefined,
        },
        {
          onSuccess: () => {
            onOpenChange(false);
            setPaymentAmount('');
            setPaymentReference('');
          },
        }
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogContent>
            <p style={{ marginBottom: '16px', color: 'var(--tl-text-secondary)' }}>
              Record a check or cash payment for this invoice.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="Payment Type">
                <Dropdown
                  value={paymentType === 'check' ? 'Check (Regions Bank)' : 'Cash'}
                  selectedOptions={[paymentType]}
                  onOptionSelect={(_, data) => setPaymentType(data.optionValue as 'check' | 'cash')}
                >
                  <Option value="check">Check (Regions Bank - 9289)</Option>
                  <Option value="cash">Cash (Cash on Hand)</Option>
                </Dropdown>
              </Field>
              <Field label="Amount">
                <Input
                  type="number"
                  value={paymentAmount}
                  onChange={(_, data) => setPaymentAmount(data.value)}
                  placeholder="0.00"
                  contentBefore={<span>$</span>}
                />
              </Field>
              {paymentType === 'check' && (
                <Field label="Check Number (Reference)">
                  <Input
                    value={paymentReference}
                    onChange={(_, data) => setPaymentReference(data.value)}
                    placeholder="e.g., 1234"
                  />
                </Field>
              )}
            </div>
            {recordPaymentMutation.isError && (
              <div style={{ color: '#d83b01', fontSize: '13px', marginTop: '8px' }}>
                <WarningRegular /> {(recordPaymentMutation.error as Error)?.message || 'Failed to record payment'}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!paymentAmount || parseFloat(paymentAmount) <= 0 || recordPaymentMutation.isPending}
              onClick={handleSubmit}
            >
              {recordPaymentMutation.isPending ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
